/* =====================================================================================
   Shared interface behaviour.

   Three things that were inconsistent across the application and are now decided once:

     useAutosave   save on a pause, confirm visibly, and never lose work to a stray click
     useDialog     focus trap, Escape to close, focus returned to whatever opened it
     Loading       one quiet affordance, not five different spinners

   Deciding these centrally is the point. Eight button classes and three overlay patterns
   happened because each screen made its own call, and the user pays for that in surprise.
   ===================================================================================== */

import { useCallback, useEffect, useRef, useState } from 'react';

/* -------------------------------------------------------------------------------------
   Autosave
   ------------------------------------------------------------------------------------- */
export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export interface Autosave<T> {
  state: SaveState;
  error: string | null;
  /** Register an edit. Starts the debounce. */
  edit: (patch: Partial<T>) => void;
  /** Force an immediate save, e.g. from an explicit button. */
  flush: () => Promise<void>;
  pending: Partial<T>;
  /** True when there are unsaved edits, for a guard elsewhere. */
  dirty: boolean;
}

/**
 * Autosave with a visible result.
 *
 * Saves 1.2 seconds after typing stops, on blur of the containing form, and on page unload.
 * The unload case is the one that matters: losing twenty minutes of edits to a mis-clicked
 * link is the single most infuriating thing an internal tool can do, so we also register a
 * beforeunload prompt while anything is unsaved.
 *
 * Autosave WITHOUT confirmation is worse than no autosave, because the user cannot tell
 * whether their work is safe. `state` drives that confirmation and callers must show it.
 */
export function useAutosave<T extends Record<string, unknown>>(
  save: (patch: Partial<T>) => Promise<void>,
  { delay = 1200 }: { delay?: number } = {},
): Autosave<T> {
  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<Partial<T>>({});
  const timer = useRef<number | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const patch = pending.current;
    if (!Object.keys(patch).length) return;
    pending.current = {};
    setState('saving'); setError(null);
    try {
      await saveRef.current(patch);
      setState('saved');
      // The confirmation fades, but only after long enough to be read.
      window.setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      // Put the edits back so a retry or an explicit save does not lose them.
      pending.current = { ...patch, ...pending.current };
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    }
  }, []);

  const edit = useCallback((patch: Partial<T>) => {
    pending.current = { ...pending.current, ...patch };
    setState('dirty');
    if (timer.current) clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), delay);
  }, [delay, flush]);

  // Warn before leaving with unsaved work, and attempt one last save.
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (Object.keys(pending.current).length) {
        void flush();
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      // Unmounting counts as leaving: save rather than discard.
      if (Object.keys(pending.current).length) void flush();
    };
  }, [flush]);

  return {
    state, error, edit, flush,
    pending: pending.current,
    dirty: state === 'dirty' || state === 'saving',
  };
}

/* -------------------------------------------------------------------------------------
   Dialogs
   ------------------------------------------------------------------------------------- */
/**
 * Makes a dialog usable by keyboard and screen reader: traps Tab inside it, closes on
 * Escape, and returns focus to whatever opened it. Without the last part a keyboard user is
 * dropped at the top of the document every time they close a form.
 */
export function useDialog(onClose: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    opener.current = document.activeElement;
    const node = ref.current;

    const focusable = () => Array.from(
      node?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
        'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    ).filter((el) => el.offsetParent !== null);

    // Move focus in, so the first Tab does not jump to the browser chrome.
    focusable()[0]?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const els = focusable();
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    document.addEventListener('keydown', onKey, true);
    // Stop the page behind scrolling under the dialog.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = overflow;
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  return ref;
}
